import React, { createContext, useState, useContext, ReactNode, useMemo, useCallback } from 'react';
import { LatLngExpression, Map as LeafletMap } from 'leaflet';
import { DEFAULT_ZOOM } from './leaflet-map.constants';
import { useMediaQuery } from '@mui/material';
import { SIDE_BAR_WIDTH } from '../../constants/sideBarWidth';

interface MapContextType {
    map: LeafletMap | null;
    setMap: (map: LeafletMap | null) => void;
    zoomToLocation: (coordinates?: LatLngExpression) => void;
    flyToLocation: (coordinates?: LatLngExpression) => void;
    disableMapInteraction: () => void;
    enableMapInteraction: () => void;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

interface MapProviderProps {
    children: ReactNode;
}

export const MapProvider: React.FC<MapProviderProps> = ({ children }) => {
    const [map, setMap] = useState<LeafletMap | null>(null);
    const isDesktop = useMediaQuery('(min-width:600px)');

    const zoomToLocation = useCallback(
        (coordinates?: LatLngExpression) => {
            if (map && coordinates) {
                if (isDesktop) {
                    const point = map.project(coordinates, DEFAULT_ZOOM);

                    const adjustedPoint = point.subtract([SIDE_BAR_WIDTH / 2, 0]);

                    const adjustedLatLng = map.unproject(adjustedPoint, DEFAULT_ZOOM);

                    map.setView(adjustedLatLng, DEFAULT_ZOOM);
                } else {
                    map.setView(coordinates, DEFAULT_ZOOM);
                }
            }
        },
        [map, isDesktop]
    );

    const flyToLocation = useCallback(
        (coordinates?: LatLngExpression) => {
            if (map && coordinates) {
                if (isDesktop) {
                    const point = map.project(coordinates, DEFAULT_ZOOM);

                    const adjustedPoint = point.subtract([SIDE_BAR_WIDTH / 2, 0]);

                    const adjustedLatLng = map.unproject(adjustedPoint, DEFAULT_ZOOM);

                    map.flyTo(adjustedLatLng, DEFAULT_ZOOM, {
                        animate: true,
                        duration: 0.5,
                    });
                } else {
                    map.flyTo(coordinates, DEFAULT_ZOOM, {
                        animate: true,
                        duration: 0.5,
                    });
                }
            }
        },
        [map, isDesktop]
    );

    const disableMapInteraction = useCallback(() => {
        if (map) {
            map.dragging.disable();
            map.touchZoom.disable();
        }
    }, [map]);

    const enableMapInteraction = useCallback(() => {
        if (map) {
            map.dragging.enable();
            map.touchZoom.enable();
        }
    }, [map]);

    const contextValue = useMemo(
        () => ({
            map,
            setMap,
            zoomToLocation,
            flyToLocation,
            disableMapInteraction,
            enableMapInteraction,
        }),
        [map, setMap, disableMapInteraction, enableMapInteraction]
    );

    return <MapContext.Provider value={contextValue}>{children}</MapContext.Provider>;
};

export const useLeafletMap = (): MapContextType => {
    const context = useContext(MapContext);
    if (context === undefined) {
        throw new Error('useMap must be used within a MapProvider');
    }
    return context;
};
